// src/modules/treasury/components/TreasuryKpiCards.jsx
// KPI summary cards for the HO dashboard.

import React from 'react';
import { Row, Col, Card, Statistic, Badge, Tooltip } from 'antd';
import {
  BranchesOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CloseCircleOutlined, FileTextOutlined, BankOutlined,
  ArrowUpOutlined, ArrowDownOutlined, MinusCircleOutlined,
} from '@ant-design/icons';

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

/**
 * @param {object} summary        Response from GET /ho-dashboard/summary
 * @param {boolean} loading
 */
export default function TreasuryKpiCards({ summary, loading }) {
  if (!summary) return null;

  const {
    totalBranches = 0,
    branchStatusCounts = {},
    pendingPaymentApprovals = 0,
    pendingClosureApprovals = 0,
    centralFundBalance = 0,
    todayCashIn = 0,
    todayCashOut = 0,
  } = summary;

  const openCount    = (branchStatusCounts.OPEN || 0) + (branchStatusCounts.REOPENED || 0);
  const pendingCount = (branchStatusCounts.PENDING_CASH_VERIFICATION || 0) + (branchStatusCounts.PENDING_CHECKER_APPROVAL || 0);
  const closedCount  = branchStatusCounts.CLOSED || 0;
  const noActivity   = branchStatusCounts.NO_DAY || 0;

  const cards = [
    {
      title: 'Branches Open',
      value: openCount,
      suffix: `/ ${totalBranches}`,
      icon: <CheckCircleOutlined />,
      color: '#52c41a',
      border: '#b7eb8f',
    },
    {
      title: 'Pending EOD / Approval',
      value: pendingCount,
      icon: <ClockCircleOutlined />,
      color: '#fa8c16',
      border: '#ffd591',
      alert: pendingCount > 0,
    },
    {
      title: 'Branches Closed',
      value: closedCount,
      icon: <CloseCircleOutlined />,
      color: '#8c8c8c',
      border: '#d9d9d9',
    },
    {
      title: 'No Activity',
      value: noActivity,
      icon: <MinusCircleOutlined />,
      color: '#bfbfbf',
      border: '#f0f0f0',
    },
    {
      title: 'Pending Payments',
      value: pendingPaymentApprovals,
      icon: <FileTextOutlined />,
      color: pendingPaymentApprovals > 0 ? '#f5222d' : '#8c8c8c',
      border: pendingPaymentApprovals > 0 ? '#ffa39e' : '#d9d9d9',
      alert: pendingPaymentApprovals > 0,
    },
    {
      title: 'Central Fund Balance',
      value: `₹${fmt(centralFundBalance)}`,
      icon: <BankOutlined />,
      color: centralFundBalance < 0 ? '#f5222d' : '#1677ff',
      border: centralFundBalance < 0 ? '#ffa39e' : '#91caff',
      isAmount: true,
    },
    {
      title: "Today's Cash In",
      value: `₹${fmt(todayCashIn)}`,
      icon: <ArrowUpOutlined />,
      color: '#52c41a',
      border: '#b7eb8f',
      isAmount: true,
    },
    {
      title: "Today's Cash Out",
      value: `₹${fmt(todayCashOut)}`,
      icon: <ArrowDownOutlined />,
      color: '#f5222d',
      border: '#ffa39e',
      isAmount: true,
    },
  ];

  return (
    <Row gutter={[12, 12]}>
      {cards.map((card) => (
        <Col key={card.title} xs={12} sm={8} md={6}>
          <Card
            size="small"
            loading={loading}
            style={{ borderColor: card.border }}
          >
            <Statistic
              title={
                <span style={{ fontSize: 12 }}>
                  {React.cloneElement(card.icon, { style: { color: card.color, marginRight: 4 } })}
                  {card.title}
                  {card.alert && (
                    <Badge dot style={{ marginLeft: 4, backgroundColor: card.color }} />
                  )}
                </span>
              }
              value={card.isAmount ? card.value : card.value}
              suffix={card.suffix}
              valueStyle={{ color: card.color, fontSize: card.isAmount ? 14 : 22, fontWeight: 600 }}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
}
